import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { CloudDivider } from "@/components/common";
import { Quote, Heart, PenLine } from "lucide-react";

export default function Testimonials() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", message: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openSubmit = () => {
    if (!user) { toast.info(t("testimonials.loginToShare")); navigate("/member"); return; }
    setForm({ name: user?.name || "", message: "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.message.trim()) { toast.error(lang === "zh" ? "请填写见证内容" : "Please write your testimonial"); return; }
    setBusy(true);
    try {
      await api.post("/testimonials", form);
      toast.success(t("testimonials.thanks"));
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
      <div className="text-center mb-6">
        <Heart className="h-9 w-9 mx-auto text-[hsl(var(--brand-vermillion))]" />
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold">{t("testimonials.title")}</h1>
        <p className="mt-2 text-muted-foreground">{t("testimonials.subtitle")}</p>
        <Button className="mt-5 bg-[hsl(var(--primary))] text-primary-foreground" onClick={openSubmit} data-testid="share-testimonial-button">
          <PenLine className="h-4 w-4 mr-1" /> {t("testimonials.submitTitle")}
        </Button>
      </div>
      <CloudDivider />

      {items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">{t("testimonials.empty")}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
          {items.map((it) => (
            <Card key={it.id} className="border-border bg-card" data-testid={`testimonial-${it.id}`}>
              <CardContent className="p-5">
                <Quote className="h-6 w-6 text-[hsl(var(--brand-gold))]" />
                <p className="mt-2 text-[15px] leading-7 text-foreground/90 whitespace-pre-line">{it.message}</p>
                <div className="mt-4 flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-[hsl(var(--brand-gold-soft))] flex items-center justify-center font-semibold text-[hsl(var(--brand-ink))]">{(it.name || "善")[0]}</div>
                  <div>
                    <div className="font-semibold text-sm">{it.name}</div>
                    <div className="text-xs text-muted-foreground">{(it.created_at || "").slice(0, 10)}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-card">
          <DialogHeader><DialogTitle>{t("testimonials.submitTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("testimonials.yourName")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="testimonial-name-input" /></div>
            <div><Label>{t("testimonials.yourMessage")}</Label><Textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="testimonial-message-input" /></div>
            <p className="text-xs text-muted-foreground">{t("testimonials.pendingNote")}</p>
            <Button className="w-full bg-[hsl(var(--primary))] text-primary-foreground" disabled={busy} onClick={submit} data-testid="testimonial-submit-button">{t("testimonials.submitBtn")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
